import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as providerPriceListItemActions from '@redux/modules/providerPriceListItem'

import defaultFormState, { requiredFields } from '@models/providerPriceListItem'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
// @ts-expect-error TS2724
import type { ProviderPriceListItemModel, ProviderPriceListItemRequestOptions } from '@models/types'

export const generateUrls = (providerPriceListItem?: Partial<ProviderPriceListItemModel>) => {
  const { id } = providerPriceListItem || {}

  return {
    editProviderPriceListItemDetailsUrl: `#/systemManager/providerPriceListItems/${id}/edit/details`,
    editProviderPriceListItemUrl: `#/systemManager/providerPriceListItems/${id}/edit/`,
    providerPriceListItemsIndexUrl: '#/systemManager/providerPriceListItems',
  }
}

type CreateProviderPriceListItemParams = {
  dispatch: AppDispatch,
  providerPriceListItemParams: Partial<ProviderPriceListItemModel>,
  requestOptions?: ProviderPriceListItemRequestOptions,
}

const createProviderPriceListItem = (params: CreateProviderPriceListItemParams) => {
  const { dispatch, providerPriceListItemParams, requestOptions } = params
  const { createProviderPriceListItem: createFn } = providerPriceListItemActions

  return dispatch(createFn(providerPriceListItemParams, requestOptions))
}

type UpdateProviderPriceListItemParams = {
  dispatch: AppDispatch,
  providerPriceListItem: ProviderPriceListItemModel,
  providerPriceListItemParams: Partial<ProviderPriceListItemModel>,
  requestOptions?: ProviderPriceListItemRequestOptions,
}

const updateProviderPriceListItem = (params: UpdateProviderPriceListItemParams) => {
  const {
    dispatch, providerPriceListItem, providerPriceListItemParams, requestOptions,
  } = params
  const { updateProviderPriceListItem: updateFn } = providerPriceListItemActions

  const updatedParams = {
    id: providerPriceListItem.id,
    ...providerPriceListItemParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DuplicateProviderPriceListItemParams = {
  dispatch: AppDispatch,
  providerPriceListItem: ProviderPriceListItemModel,
  requestOptions?: ProviderPriceListItemRequestOptions,
}

const duplicateProviderPriceListItem = (params: DuplicateProviderPriceListItemParams) => {
  const { dispatch, providerPriceListItem, requestOptions } = params
  const { duplicateProviderPriceListItem: duplicateFn } = providerPriceListItemActions

  return dispatch(duplicateFn(providerPriceListItem, requestOptions))
}

type DeleteProviderPriceListItemParams = {
  dispatch: AppDispatch,
  providerPriceListItem: DeleteParams<ProviderPriceListItemModel>,
}

const deleteProviderPriceListItem = (params: DeleteProviderPriceListItemParams) => {
  const { dispatch, providerPriceListItem } = params
  const { deleteProviderPriceListItem: deleteFn } = providerPriceListItemActions

  return dispatch(deleteFn(providerPriceListItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderPriceListItemForm(
  providerPriceListItem: Partial<ProviderPriceListItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const providerPriceListItemForm = useForm(
    defaultFormState,
    { entity: providerPriceListItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [providerPriceListItem.id, providerPriceListItem.cache_key],
  )

  return {
    ...providerPriceListItemForm,
  }
}

export const useRelations = (providerPriceListItem: Partial<ProviderPriceListItemModel> = {}) => {
  const { provider_price_list_id } = providerPriceListItem

  const { providerPriceLists } = useSelector(reduxState => reduxState.entities)

  const providerPriceList = provider_price_list_id && providerPriceLists[provider_price_list_id]
    ? providerPriceLists[provider_price_list_id] : {}

  return {
    providerPriceList,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListItemRequestOptions,
}

function useProviderPriceListItem(initEntity: Partial<ProviderPriceListItemModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: providerPriceListItem }: { entity: ProviderPriceListItemModel} = useLatestEntity(initEntity, 'providerPriceListItems')
  const { id } = providerPriceListItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { providerPriceList } = useRelations(providerPriceListItem)

  useReduxAction(
    'providerPriceListItems',
    'loadProviderPriceListItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.providerPriceListItems)

  const serviceProviders = [
    { key: 'domain_group', title: 'Domain Group' },
  ]

  return {
    callbacks: {
      createProviderPriceListItem: (
        providerPriceListItemParams: Partial<ProviderPriceListItemModel>,
        entityOptions?: ProviderPriceListItemRequestOptions,
      ) => (
        createProviderPriceListItem({ providerPriceListItemParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditProviderPriceListItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditProviderPriceListItemModal',
        payload: { providerPriceListItem },
      }),
      deleteProviderPriceListItem: () => deleteProviderPriceListItem({ dispatch, providerPriceListItem }),
      duplicateProviderPriceListItem: (entityOptions?: ProviderPriceListItemRequestOptions) => (
        duplicateProviderPriceListItem({
          dispatch,
          providerPriceListItem,
          requestOptions: entityOptions,
        })
      ),
      toggleItemVisibility: (entityOptions?: ProviderPriceListItemRequestOptions) => (
        updateProviderPriceListItem({
          dispatch,
          providerPriceListItem,
          providerPriceListItemParams: { hidden: !providerPriceListItem.hidden },
          requestOptions: entityOptions,
        })
      ),
      updateProviderPriceListItem: (
        providerPriceListItemParams: Partial<ProviderPriceListItemModel>,
        entityOptions?: ProviderPriceListItemRequestOptions,
      ) => (
        updateProviderPriceListItem({
          providerPriceListItem, providerPriceListItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading: loading && !loadedIds.includes(providerPriceListItem.id),
    providerPriceList,
    providerPriceListItem,
    serviceProviders,
    updating,
    urls: generateUrls(providerPriceListItem),
  }
}

export default useProviderPriceListItem
